import { useEffect, useRef, useState } from 'react';

/**
 * Local-state mirror for a controlled input that pushes upstream after the user
 * stops typing. Cuts Supabase write traffic from one-per-keystroke to one-per-pause
 * without sacrificing instant typing feedback in the input itself.
 *
 * - `upstream` — the source-of-truth value coming from props/context.
 * - `onCommit` — called with the new value after `delay` ms of no edits.
 * - `delay`    — debounce window in ms (default 350).
 *
 * Returns `[value, setValue]` to wire directly to a controlled `<input>`.
 *
 * If `upstream` changes externally (e.g. AI generated scope, another tab edited
 * the same record) and the user isn't currently typing, the local state is
 * re-synced. Active edits win until the debounce flushes.
 */
export function useDebouncedField<T>(
    upstream: T,
    onCommit: (next: T) => void,
    delay = 350,
): [T, (next: T) => void] {
    const [local, setLocal] = useState<T>(upstream);
    const isEditingRef = useRef(false);

    // External upstream changes → sync into local, but only when user isn't actively typing
    useEffect(() => {
        if (!isEditingRef.current) {
            setLocal(upstream);
        }
    }, [upstream]);

    // Debounce local → upstream
    useEffect(() => {
        if (!isEditingRef.current) return;
        if (local === upstream) {
            isEditingRef.current = false;
            return;
        }
        const t = setTimeout(() => {
            onCommit(local);
            isEditingRef.current = false;
        }, delay);
        return () => clearTimeout(t);
    }, [local, upstream, onCommit, delay]);

    const setValue = (next: T) => {
        isEditingRef.current = true;
        setLocal(next);
    };

    return [local, setValue];
}
